import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronLeft, Play, Pause, Download, Check, X, Music, Sparkles, Wand2, Type, Scissors, Volume2, Timer, Zap, Trash2, Layers, Cloud, CloudOff, Maximize2, Minimize2, ArrowLeft, FolderOpen } from 'lucide-react';
import { apiClient } from '../../utils/apiClient';
import MagicCreate from '../generation/MagicCreate'; // 🪄 Magic Flow Integration

/**
 * 🎬 BEATLEAP PRO STUDIO V7 - UNIVERSAL PERSISTENCE
 * + Auto-Save zu MongoDB (1.5s Debounce)
 * + Transform-Werte (x, y, scale) werden gespeichert
 * + Projekt-Wiederaufnahme mit exaktem Stand
 */

export default function BeatleapStudio({ isOpen, onClose, onSaveToProjects, initialClips = [], selectedTrack: initialTrack, onExport, projectId: initialProjectId, avatarId: propAvatarId }) {
  // Avatar ID aus Props oder URL holen
  const avatarId = propAvatarId || new URLSearchParams(window.location.search).get('avatar_id') || localStorage.getItem('snapshot_current_avatar_id');

  // 🔒 Admin-Check: MagicCreate/SnapCut nur für Master-Admin
  const studioUser = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
  const isMasterAdmin = studioUser.email === 'info@snapshotpro.ai';
  // --- ENGINE REFS ---
  const masterTimeRef = useRef(0);
  const rafRef = useRef(null);
  const lastTickTime = useRef(0);
  const isPlayingRef = useRef(false);
  const timelineRef = useRef(null);
  
  // 🚀 ZERO-LAG REFS (Direct DOM Updates)
  const timeDisplayRef = useRef(null);
  const rulerRef = useRef(null);

  // --- STATE ---
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [clips, setClips] = useState(initialClips);
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [showLoading, setShowLoading] = useState(true); // Loading für alle außer leeres Studio (wird unten gecheckt)
  const [loadProgress, setLoadProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(initialTrack);
  
  // ⚡ ZOOM: Pixel pro Sekunde
  const [zoom] = useState(50);
  
  // ✂️ TRIMMING & SCRUBBING
  const [isTrimming, setIsTrimming] = useState(null);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const wasPlayingBeforeScrubRef = useRef(false); // 🎬 Merkt sich Play-Status vor Scrub
  
  // 🎨 TRANSFORM MODE (Pan & Zoom)
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0, distance: 0 });
  
  // 📁 PROJECT PERSISTENCE
  const [projectId, setProjectId] = useState(initialProjectId || null);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'error'
  const saveTimeoutRef = useRef(null);
  
  // 🎵 AUDIO TRACKS (CapCut-Style Multi-Track Audio)
  const [audioTracks, setAudioTracks] = useState([]);
  const [showMusicLibrary, setShowMusicLibrary] = useState(false);
  const [showMagicCreate, setShowMagicCreate] = useState(false); // 🪄 Magic Flow
  const [selectedAudioId, setSelectedAudioId] = useState(null);
  const [isAudioTrimming, setIsAudioTrimming] = useState(null);
  const audioRefs = useRef({});
  const audioContextRef = useRef(null);
  const lastAudioSeekRef = useRef(0); // Throttle für Scrubbing
  
  // 🔤 TEXT TRACKS (CapCut-Style Text Layer)
  const [textTracks, setTextTracks] = useState([]);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [isEditingText, setIsEditingText] = useState(null); // ID des Textes der getippt wird
  const [isTextTrimming, setIsTextTrimming] = useState(null);
  
  // 🔍 FULLSCREEN PREVIEW MODE
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 🧲 SNAP-TO-CENTER STATE
  const [snapLines, setSnapLines] = useState({ x: false, y: false });
  const SNAP_THRESHOLD = 10; // Pixel-Schwelle für Magnet-Effekt

  // 🪄 KI-ANIMATION STATE (Astria Kling)
  const [animatingClipIds, setAnimatingClipIds] = useState(new Set());
  const [animateModal, setAnimateModal] = useState({ open: false, clipId: null });
  const [animatePrompt, setAnimatePrompt] = useState('');
  const [animateLang, setAnimateLang] = useState('de');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  // animationPollRefs wird weiter unten definiert (für parallele Animationen)

  // 🔇 STOP ALL AUDIO - Wichtig beim Schließen!
  const stopAllAudio = useCallback(() => {
    setIsPlaying(false);
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    console.log('🔇 Alle Audio gestoppt');
  }, []);

  // 🧹 CLEANUP: Audio stoppen wenn Component unmounted
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      audioRefs.current = {};
    };
  }, []);

  // 🎵 WAVEFORM PEAKS CACHE (für Performance)
  const waveformCacheRef = useRef({});

  // 🎵 AUDIO ANALYSE - Extrahiert Peaks aus Audio-Datei für Waveform
  const analyzeAudioPeaks = useCallback(async (audioUrl, trackId, samplesPerSecond = 10) => {
    // Check Cache
    if (waveformCacheRef.current[trackId]) {
      console.log(`🎵 Waveform aus Cache: ${trackId}`);
      return waveformCacheRef.current[trackId];
    }

    try {
      console.log(`🎵 Analysiere Audio für Waveform: ${trackId}`);
      
      // Audio-Datei laden
      const response = await fetch(audioUrl);
      const arrayBuffer = await response.arrayBuffer();
      
      // AudioContext für Analyse
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Mono-Channel extrahieren (oder Stereo zu Mono mischen)
      const channelData = audioBuffer.getChannelData(0);
      const sampleRate = audioBuffer.sampleRate;
      const duration = audioBuffer.duration;
      
      // Samples pro Peak berechnen
      const totalPeaks = Math.ceil(duration * samplesPerSecond);
      const samplesPerPeak = Math.floor(channelData.length / totalPeaks);
      
      const peaks = [];
      
      for (let i = 0; i < totalPeaks; i++) {
        const start = i * samplesPerPeak;
        const end = Math.min(start + samplesPerPeak, channelData.length);
        
        // Max-Amplitude in diesem Segment finden
        let max = 0;
        for (let j = start; j < end; j++) {
          const abs = Math.abs(channelData[j]);
          if (abs > max) max = abs;
        }
        
        peaks.push(max);
      }
      
      // Normalisieren auf 0-1 Bereich
      const maxPeak = Math.max(...peaks);
      const normalizedPeaks = peaks.map(p => maxPeak > 0 ? p / maxPeak : 0);
      
      // Cache speichern
      waveformCacheRef.current[trackId] = normalizedPeaks;
      
      console.log(`🎵 Waveform analysiert: ${normalizedPeaks.length} Peaks für ${duration.toFixed(1)}s`);
      audioContext.close();
      
      return normalizedPeaks;
    } catch (err) {
      console.error('❌ Waveform Analyse fehlgeschlagen:', err);
      return null;
    }
  }, []);

  // 🎯 SMART SELECT: Deselektiert alle anderen beim Auswählen
  const selectClip = useCallback((id) => { setSelectedClipId(id); setSelectedAudioId(null); setSelectedTextId(null); }, []);
  const selectAudio = useCallback((id) => { setSelectedAudioId(id); setSelectedClipId(null); setSelectedTextId(null); }, []);
  const selectText = useCallback((id) => { setSelectedTextId(id); setSelectedClipId(null); setSelectedAudioId(null); }, []);
  const deselectAll = useCallback(() => { setSelectedClipId(null); setSelectedAudioId(null); setSelectedTextId(null); }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 1. DIE ABSOLUTE ZEIT-LOGIK
  // ═══════════════════════════════════════════════════════════════════════════
  
  const clipsWithTiming = useMemo(() => {
    let acc = 0;
    return clips.map((clip, i) => {
      const duration = Number(clip.duration) || 3.0;
      const start = acc;
      const end = acc + duration;
      acc = end;
      return { ...clip, start, end, index: i };
    });
  }, [clips]);

  const totalDuration = useMemo(() => {
    // Video-Ende
    const videoEnd = clipsWithTiming.length > 0 
      ? clipsWithTiming[clipsWithTiming.length - 1].end 
      : 0;
    // Audio-Ende (das am weitesten rechts liegende Audio)
    const audioEnd = audioTracks.reduce(
      (max, track) => Math.max(max, track.startTime + track.duration), 
      0
    );
    // Timeline = längstes Element
    return Math.max(videoEnd, audioEnd);
  }, [clipsWithTiming, audioTracks]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📏 2. PRECISION RULER
  // ═══════════════════════════════════════════════════════════════════════════
  
  const renderRuler = useCallback(() => {
    const seconds = Math.ceil(totalDuration) + 2;
    const ticks = [];
    
    for (let i = 0; i <= seconds; i++) {
      ticks.push(
        <div key={i} className="relative flex-shrink-0" style={{ width: `${zoom}px` }}>
          <div className="h-2 w-[1px] bg-gray-500" />
          <span className="absolute top-3 left-0 -translate-x-1/2 text-[9px] text-gray-500 font-mono">
            {i}s
          </span>
          <div className="absolute top-0 left-1/2 h-1 w-[1px] bg-gray-700" />
        </div>
      );
    }
    return ticks;
  }, [totalDuration, zoom]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📁 AUTO-SAVE ENGINE (Cloud-Sync mit 1.5s Debounce)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Projekt-Snapshot erstellen (ALLES was gespeichert wird - Champions League!)
  const getProjectSnapshot = useCallback(() => {
    return {
      last_modified: new Date().toISOString(),
      project_config: {
        // 🎬 CLIPS (Bilder/Videos)
        clips: clips.map(c => ({
          id: c.id,
          url: c.url,
          thumbnail: c.thumbnail,
          type: c.type || 'image',
          videoUrl: c.videoUrl || null,  // 🪄 Für animierte Clips!
          duration: c.duration || 3,
          // Transform-Werte
          x: c.x || 0,
          y: c.y || 0,
          scale: c.scale || 1,
          // Trimm-Punkte
          trimStart: c.trimStart || 0,
          trimEnd: c.trimEnd || c.duration || 3,
          // Zukünftige Features
          vfx: c.vfx || [],
          filters: c.filters || {}
        })),
        // 🎵 AUDIO TRACKS (Musik)
        audioTracks: audioTracks.map(a => ({
          id: a.id,
          name: a.name,
          url: a.url,
          startTime: a.startTime || 0,
          duration: a.duration || 30,
          volume: a.volume || 1,
          trimStart: a.trimStart || 0
        })),
        // 🔤 TEXT TRACKS (Text-Overlays)
        textTracks: textTracks.map(t => ({
          id: t.id,
          content: t.content || 'Text',
          x: t.x || 0,
          y: t.y || 0,
          scale: t.scale || 1,
          rotation: t.rotation || 0,
          startTime: t.startTime || 0,
          duration: t.duration || 3,
          fontFamily: t.fontFamily || 'Inter',
          fontSize: t.fontSize || 32,
          color: t.color || '#FFFFFF'
        })),
        music: selectedTrack,
        zoom: zoom,
        metadata: {}
      }
    };
  }, [clips, audioTracks, textTracks, selectedTrack, zoom]);

  // Auto-Save Funktion
  const saveToCloud = useCallback(async () => {
    if (!projectId || clips.length === 0) return;
    
    try {
      setSaveStatus('saving');
      const snapshot = getProjectSnapshot();
      await apiClient.post(`/api/beatleap/save-draft/${projectId}`, snapshot);
      setSaveStatus('saved');
      console.log('☁️ Projekt gespeichert');
    } catch (err) {
      console.error('❌ Speicherfehler:', err);
      setSaveStatus('error');
    }
  }, [projectId, clips, getProjectSnapshot]);

  // Debounced Auto-Save bei Änderungen
  useEffect(() => {
    if (!projectId || clips.length === 0) return;
    
    // Neue Projekt-ID generieren wenn keine vorhanden
    if (!projectId && clips.length > 0) {
      setProjectId(`proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    }
    
    // Debounce: 1.5s nach letzter Änderung speichern
    clearTimeout(saveTimeoutRef.current);
    setSaveStatus('saving');
    
    saveTimeoutRef.current = setTimeout(() => {
      saveToCloud();
    }, 1500);
    
    return () => clearTimeout(saveTimeoutRef.current);
  }, [clips, selectedTrack, projectId, saveToCloud]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 📂 PROJEKT-LOADER (Zukunftssicher mit Fallbacks)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Mapper-Funktion: Normalisiert Clip-Daten mit Fallbacks für alte Projekte
  const mapClipWithDefaults = useCallback((clip) => ({
    // Alle vorhandenen Werte übernehmen (Zukunftssicher!)
    ...clip,
    // Pflichtfelder mit Fallbacks
    id: clip.id || `clip_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    url: clip.url,
    thumbnail: clip.thumbnail || clip.url,
    type: clip.type || 'image',
    videoUrl: clip.videoUrl || null,  // 🪄 Animierte Clips!
    duration: clip.duration ?? 3,
    maxDuration: clip.maxDuration ?? (clip.videoUrl ? (clip.duration ?? 5) : null), // videoUrl = animiert
    // Transform-Werte mit Fallbacks (für alte Projekte ohne diese Werte)
    x: clip.x ?? 0,
    y: clip.y ?? 0,
    scale: clip.scale ?? 1,
    // Trimm-Punkte mit Fallbacks
    trimStart: clip.trimStart ?? 0,
    trimEnd: clip.trimEnd ?? (clip.duration ?? 3),
    // Zukünftige Features (automatisch übernommen durch ...clip)
    vfx: clip.vfx || [],
    filters: clip.filters || {}
  }), []);

  // Projekt von MongoDB laden (KOMPLETT - Clips + Audio + Text!)
  const loadProject = useCallback(async (projectIdToLoad) => {
    try {
      console.log(`📂 Lade Projekt: ${projectIdToLoad}`);
      const response = await apiClient.get(`/api/beatleap/project/${projectIdToLoad}`);
      const data = await response.json();  // ← WICHTIG: fetch Response → JSON
      
      if (data.success && data.project) {
        const project = data.project;
        const config = project.project_config || {};
        
        // State-Reset beim Projektwechsel (kein Geister-Rahmen!)
        setSelectedClipId(null);
        setSelectedAudioId(null);
        setSelectedTextId(null);
        setCurrentTime(0);
        masterTimeRef.current = 0;
        
        // 🎬 Clips mit Fallbacks mappen
        const loadedClips = (config.clips || []).map(mapClipWithDefaults);
        setClips(loadedClips);
        
        // 🎵 Audio Tracks laden (mit Fallbacks)
        const loadedAudio = (config.audioTracks || []).map(a => ({
          id: a.id || `audio_${Date.now()}`,
          name: a.name || 'Audio',
          url: a.url || '',
          startTime: a.startTime || 0,
          duration: a.duration || 30,
          volume: a.volume || 1,
          trimStart: a.trimStart || 0,
          audioOffset: a.audioOffset || 0
        }));
        setAudioTracks(loadedAudio);
        
        // 🎵 Audio-Elemente erstellen + Waveform analysieren für geladene Tracks!
        loadedAudio.forEach(track => {
          if (track.url) {
            const audio = new Audio();
            audio.preload = 'auto';
            audio.crossOrigin = 'anonymous';
            audio.preservesPitch = false;
            audio.src = track.url;
            audio.load();
            audioRefs.current[track.id] = audio;
            console.log(`🎵 Audio-Element erstellt für: ${track.name}`);
            
            // 🎵 Waveform analysieren (async)
            analyzeAudioPeaks(track.url, track.id, 20).then(peaks => {
              if (peaks) {
                setAudioTracks(prev => prev.map(t => 
                  t.id === track.id ? { ...t, waveformPeaks: peaks } : t
                ));
              }
            });
          }
        });
        
        // 🔤 Text Tracks laden (mit Fallbacks)
        const loadedText = (config.textTracks || []).map(t => ({
          id: t.id || `text_${Date.now()}`,
          content: t.content || 'Text',
          x: t.x || 0,
          y: t.y || 0,
          scale: t.scale || 1,
          rotation: t.rotation || 0,
          startTime: t.startTime || 0,
          duration: t.duration || 3,
          fontFamily: t.fontFamily || 'Inter',
          fontSize: t.fontSize || 32,
          color: t.color || '#FFFFFF'
        }));
        setTextTracks(loadedText);
        
        // Musik-Track laden (legacy)
        if (config.music) {
          setSelectedTrack(config.music);
        }
        
        // Projekt-ID setzen
        setProjectId(projectIdToLoad);
        
        console.log(`✅ Projekt geladen: ${loadedClips.length} Clips, ${loadedAudio.length} Audio, ${loadedText.length} Text`);
        return true;
      }
    } catch (err) {
      console.error('❌ Projekt laden fehlgeschlagen:', err);
    }
    return false;
  }, [mapClipWithDefaults, analyzeAudioPeaks]);

  // Projekt-ID generieren beim ersten Clip (nur wenn kein existierendes Projekt)
  useEffect(() => {
    if (clips.length > 0 && !projectId) {
      setProjectId(`proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    }
  }, [clips, projectId]);

  // --- INIT: Projekt laden oder neue Clips initialisieren ---
  useEffect(() => {
    // Wenn eine Projekt-ID übergeben wurde, lade das Projekt
    if (initialProjectId && isOpen) {
      loadProject(initialProjectId);
    }
    // Sonst: Neue Clips mit Defaults initialisieren
    else if (initialClips.length > 0 && clips.length === 0) {
      const mappedClips = initialClips.map(mapClipWithDefaults);
      setClips(mappedClips);
    }
  }, [initialProjectId, initialClips, isOpen, loadProject, mapClipWithDefaults, clips.length]);

  // --- LOADING SCREEN ---
  useEffect(() => {
    if (!isOpen) return;
    
    // Leeres Studio = kein Loading
    if (initialClips.length === 0 && !initialProjectId) {
      setShowLoading(false);
      return;
    }
    
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 100) {
        setLoadProgress(100);
        clearInterval(interval);
        setTimeout(() => setShowLoading(false), 500);
      } else {
        setLoadProgress(Math.floor(progress));
      }
    }, 200);
    return () => clearInterval(interval);
  }, [isOpen, initialClips.length, initialProjectId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎵 AUDIO-SYNCHRONISATION (Audio spielt synchron zum Video)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // 🎵 AUDIO SYNC - Spielt nur wenn Playhead in Track-Bereich ist!
  useEffect(() => {
    audioTracks.forEach(track => {
      const audio = audioRefs.current[track.id];
      if (!audio) return;
      
      const trackEnd = track.startTime + track.duration;
      const isInRange = currentTime >= track.startTime && currentTime < trackEnd;
      
      if (isPlaying && isInRange) {
        // Track ist aktiv - Play!
        const audioTime = currentTime - track.startTime + (track.audioOffset || 0);
        if (Math.abs(audio.currentTime - audioTime) > 0.5) {
          audio.currentTime = Math.max(0, audioTime);
        }
        if (audio.paused && audio.readyState >= 2) {
          audio.play().catch(() => {});
        }
      } else {
        // Track nicht aktiv oder paused - Stop!
        if (!audio.paused) {
          audio.pause();
        }
      }
    });
  }, [isPlaying, currentTime, audioTracks]);

  // 🎬 VIDEO PLAYBACK - Jetzt per ref-Callback im JSX gesteuert (CapCut-Style)
  // Kein separater Effect mehr nötig - alles inline im Video-Element

  // 🔊 AudioContext Warmup (für Hardware-Beschleunigung)
  const warmupAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        // Resume wenn suspended (iOS/Safari)
        if (audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
        console.log('🔊 AudioContext aktiviert');
      } catch (e) {
        console.log('AudioContext nicht verfügbar');
      }
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🪄 KI-ANIMATION (Runway Gen-3) - Bild → Video
  // ═══════════════════════════════════════════════════════════════════════════
  
  // 🪄 Generiert Prompt-Vorschlag mit GPT
  const generatePromptSuggestion = useCallback(async () => {
    const clip = clips.find(c => c.id === animateModal.clipId);
    if (!clip) {
      console.log('No clip found for', animateModal.clipId);
      return;
    }
    
    setIsGeneratingPrompt(true);
    try {
      const response = await apiClient.post('/api/beatleap/suggest-prompt', {
        image_url: clip.thumbnail || clip.src
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data?.suggested_prompt) {
          setAnimatePrompt(data.suggested_prompt);
        }
      } else {
        console.error('Suggest failed:', response.status);
      }
    } catch (error) {
      console.error('Prompt suggestion error:', error);
    } finally {
      setIsGeneratingPrompt(false);
    }
  }, [clips, animateModal.clipId]);

  // Öffnet das Animate-Modal
  const animateClip = useCallback((clipId) => {
    const clip = clips.find(c => c.id === clipId);
    if (!clip || clip.type === 'video') return;
    
    // Modal öffnen
    setAnimateModal({ open: true, clipId });
    setAnimatePrompt('');
    setIsGeneratingPrompt(false);
  }, [clips]);

  // Startet die Animation (nach Modal-Bestätigung)
  const startAnimation = useCallback(async () => {
    const clipId = animateModal.clipId;
    const clip = clips.find(c => c.id === clipId);
    if (!clip) return;
    
    setAnimateModal({ open: false, clipId: null });
    
    try {
      setAnimatingClipIds(prev => new Set([...prev, clipId]));
      
      // Generating-State auf Clip setzen
      setClips(prev => prev.map(c => 
        c.id === clipId ? { ...c, isGenerating: true, generationProgress: 0 } : c
      ));
      
      // API Call zu Astria Kling
      const response = await apiClient.post('/api/beatleap/animate', {
        image_url: clip.url,
        clip_id: clipId,
        auto_mode: false,
        user_prompt: animatePrompt,
        language: animateLang
      });
      
      const data = await response.json();

      if (!response.ok || !data.success || !data.jobId) {
        const detail = data.detail || 'Animation konnte nicht gestartet werden';
        // Credit-specific error message
        if (response.status === 402 || (typeof detail === 'string' && (detail.includes('SNAP') || detail.includes('genügend') || detail.includes('credit')))) {
          throw new Error('Nicht genügend SNAPs. Bitte lade dein Guthaben auf (/credits).');
        }
        throw new Error(typeof detail === 'string' ? detail : 'Animation konnte nicht gestartet werden');
      }
      
      console.log('🪄 Animation gestartet:', data.jobId);
      
      // Job-ID auf Clip speichern
      setClips(prev => prev.map(c => 
        c.id === clipId ? { ...c, runwayJobId: data.jobId, generationProgress: 10 } : c
      ));
      
      // Polling starten
      pollAnimationStatus(clipId, data.jobId);
      
    } catch (error) {
      console.error('❌ Animation Fehler:', error);
      alert('Animation fehlgeschlagen: ' + error.message);
      
      // Reset Clip State
      setClips(prev => prev.map(c => 
        c.id === clipId ? { ...c, isGenerating: false, generationProgress: 0 } : c
      ));
      setAnimatingClipIds(prev => { const next = new Set(prev); next.delete(clipId); return next; });
    }
  }, [clips, animateModal.clipId, animatePrompt, animateLang]);
  
  // Refs für mehrere parallele Polls
  const animationPollRefs = useRef({});
  
  const pollAnimationStatus = useCallback((clipId, jobId) => {
    // Clear existing poll for THIS clip only
    if (animationPollRefs.current[clipId]) {
      clearTimeout(animationPollRefs.current[clipId]);
    }
    
    const poll = async () => {
      try {
        const response = await apiClient.get(`/api/beatleap/animate-status/${jobId}`);
        const data = await response.json();
        
        console.log('📊 Animation Status:', data.status, data.progress + '%', 'videoUrl:', data.videoUrl);
        
        if (data.status === 'completed' && data.videoUrl) {
          // 🎉 ERFOLG! Asset-Swap: Bild → Video
          // Video-Dauer: Runway Gen-3 = 5 Sekunden FEST (Runway Standard)
          const VIDEO_DURATION = 5.0;
          
          console.log('🎬 Animation fertig! Duration:', VIDEO_DURATION, 'URL:', data.videoUrl);
          
          setClips(prev => prev.map(c => 
            c.id === clipId ? { 
              ...c,  // Behalte Transform-Werte (x, y, scale)
              type: 'video',
              videoUrl: data.videoUrl,
              thumbnail: data.thumbnail || c.url,
              duration: VIDEO_DURATION,  // 🎬 IMMER 5 Sekunden für Runway Videos!
              maxDuration: VIDEO_DURATION, // 🎬 Maximale Dauer = echte Video-Länge (nicht verlängerbar!)
              isGenerating: false,
              generationProgress: 100,
              runwayJobId: null
            } : c
          ));
          
          setAnimatingClipIds(prev => { const next = new Set(prev); next.delete(clipId); return next; });
          
          // Haptic Feedback
          if (navigator.vibrate) navigator.vibrate([50, 50, 100]);
          
          console.log('✅ Animation fertig! Video:', data.videoUrl);
          
        } else if (data.status === 'failed') {
          // ❌ Fehlgeschlagen
          setClips(prev => prev.map(c => 
            c.id === clipId ? { ...c, isGenerating: false, generationProgress: 0 } : c
          ));
          setAnimatingClipIds(prev => { const next = new Set(prev); next.delete(clipId); return next; });
          alert('Animation fehlgeschlagen: ' + (data.message || 'Unbekannter Fehler'));
          
        } else {
          // ⏳ Noch am Verarbeiten - Progress updaten
          setClips(prev => prev.map(c => 
            c.id === clipId ? { ...c, generationProgress: data.progress || 50 } : c
          ));
          
          // Weiter pollen (5 Sekunden)
          animationPollRefs.current[clipId] = setTimeout(poll, 5000);
        }
        
      } catch (error) {
        console.error('❌ Poll Fehler:', error);
        // Bei Netzwerkfehler: trotzdem weiter versuchen
        animationPollRefs.current[clipId] = setTimeout(poll, 8000);
      }
    };
    
    // Erstes Poll nach 3 Sekunden (Runway braucht Zeit zum Starten)
    animationPollRefs.current[clipId] = setTimeout(poll, 3000);
  }, []);
  
  // Cleanup Polling bei Unmount
  useEffect(() => {
    return () => {
      Object.values(animationPollRefs.current).forEach(t => clearTimeout(t));
    };
  }, []);

  // Audio-Track hinzufügen
  const addAudioTrack = useCallback(async (song) => {
    // AudioContext warmup beim ersten Audio
    warmupAudioContext();
    
    const trackId = `audio_${Date.now()}`;
    
    // 🎵 Optimiertes Audio-Element erstellen
    const audio = new Audio();
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audio.preservesPitch = false; // Reduziert CPU-Last
    audio.src = song.url;
    
    // 🎵 ECHTE DURATION holen wenn Metadaten geladen
    audio.onloadedmetadata = () => {
      const realDuration = audio.duration;
      console.log(`🎵 Echte Song-Länge: ${song.name} = ${realDuration.toFixed(2)}s`);
      
      // Track mit ECHTER Duration updaten
      setAudioTracks(prev => prev.map(t => 
        t.id === trackId ? { ...t, duration: realDuration } : t
      ));
    };
    
    // Preload starten
    audio.load();
    audioRefs.current[trackId] = audio;
    
    // 🎵 NUR EINE TONSPUR erlaubt - alte Audio stoppen & löschen
    stopAllAudio();
    
    // Track erstmal mit geschätzter Duration hinzufügen (wird durch onloadedmetadata korrigiert)
    const newTrack = {
      ...song,
      startTime: masterTimeRef.current, // Startet wo der Playhead steht
      audioOffset: 0, // 🎵 Wo IM SONG gestartet wird (für Trim-Left)
      id: trackId,
      waveformPeaks: null // Wird async geladen
    };
    setAudioTracks([newTrack]); // ERSETZT alle alten Tracks!
    setSelectedAudioId(null);
    setShowMusicLibrary(false);
    
    console.log(`🎵 Audio hinzugefügt: ${song.name} bei ${newTrack.startTime.toFixed(2)}s`);
    
    // 🎵 WAVEFORM ANALYSE (async, im Hintergrund)
    analyzeAudioPeaks(song.url, trackId, 20).then(peaks => {
      if (peaks) {
        setAudioTracks(prev => prev.map(t => 
          t.id === trackId ? { ...t, waveformPeaks: peaks } : t
        ));
      }
    });
  }, [warmupAudioContext, analyzeAudioPeaks]);

  // Audio-Track entfernen
  const removeAudioTrack = useCallback((trackId) => {
    setAudioTracks(prev => prev.filter(t => t.id !== trackId));
    setSelectedAudioId(null);
    if (audioRefs.current[trackId]) {
      audioRefs.current[trackId].pause();
      delete audioRefs.current[trackId];
    }
  }, []);

  // 🎵 AUDIO INTERACTION (Ghost-Playhead - Absolute Koordinaten)
  const handleAudioInteraction = useCallback((e, trackId, type) => {
    e.stopPropagation();
    
    // 🎯 PRÄZISIONS-CHECK: Nur reagieren wenn Touch wirklich auf dem Element ist
    const target = e.currentTarget;
    if (target) {
      const rect = target.getBoundingClientRect();
      const touchX = e.touches ? e.touches[0].clientX : e.clientX;
      const touchY = e.touches ? e.touches[0].clientY : e.clientY;
      
      // 10px Toleranz für dicke Finger
      const tolerance = 10;
      if (touchX < rect.left - tolerance || touchX > rect.right + tolerance ||
          touchY < rect.top - tolerance || touchY > rect.bottom + tolerance) {
        console.log('🚫 Touch außerhalb Hitbox ignoriert:', { touchX, touchY, rect: { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom } });
        return; // Touch war NICHT auf dem Element!
      }
    }
    
    // 📳 Haptisches Feedback
    if (navigator.vibrate) navigator.vibrate(5);
    
    const track = audioTracks.find(t => t.id === trackId);
    if (!track || !timelineRef.current) return;
    
    const initialEndTime = track.startTime + track.duration;
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const initialStartTime = track.startTime;
    const initialAudioOffset = track.audioOffset || 0; // 🎵 Für Audio-Trimming
    
    if (type !== 'move') setIsAudioTrimming(type);

    const onMove = (ev) => {
      if (ev.cancelable) ev.preventDefault();
      
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      
      // ⚡ LIVE RECT - JEDEN FRAME NEU HOLEN!
      const rect = timelineRef.current.getBoundingClientRect();
      const scrollLeft = timelineRef.current.scrollLeft;
      
      // ⚡ ABSOLUTE POSITION: Finger relativ zur Timeline + Scroll
      // WICHTIG: 50vw Start-Padding abziehen!
      const paddingLeft = window.innerWidth / 2;
      const absoluteX = clientX - rect.left + scrollLeft - paddingLeft;
      const currentSecond = absoluteX / zoom;
      
      // 🐛 DEBUG
      console.log(`TRIM: clientX=${clientX.toFixed(0)}, scrollLeft=${scrollLeft.toFixed(0)}, padding=${paddingLeft.toFixed(0)}, absoluteX=${absoluteX.toFixed(0)}, sec=${currentSecond.toFixed(2)}`);

      setAudioTracks(prev => prev.map(t => {
        if (t.id !== trackId) return t;
        
        if (type === 'move') {
          // 🎯 MOVE: Delta-basiert
          const deltaX = clientX - startX;
          const deltaTime = deltaX / zoom;
          return { ...t, startTime: Math.max(0, initialStartTime + deltaTime) };
          
        } else if (type === 'trim-right') {
          // ⚡ TRIM RIGHT: Dauer = Finger-Sekunde minus Start-Sekunde
          const newDuration = currentSecond - t.startTime;
          return { ...t, duration: Math.max(0.1, newDuration) };
          
        } else if (type === 'trim-left') {
          // ⚡ TRIM LEFT: Start im Video UND im Audio verschieben!
          const newStart = Math.max(0, currentSecond);
          const trimDelta = newStart - initialStartTime; // Wie viel nach rechts gezogen
          const newAudioOffset = Math.max(0, initialAudioOffset + trimDelta); // Song startet später
          return { 
            ...t, 
            startTime: newStart, 
            duration: Math.max(0.1, initialEndTime - newStart),
            audioOffset: newAudioOffset // 🎵 Song startet bei dieser Sekunde im Audio-File
          };
        }
        return t;
      }));
    };

    const onEnd = () => {
      setIsAudioTrimming(null);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  }, [audioTracks, zoom]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔤 TEXT INTERACTION (Champions League Precision)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Text-Track hinzufügen
  const addTextTrack = useCallback(() => {
    const newText = {
      id: `text_${Date.now()}`,
      content: '',
      startTime: masterTimeRef.current, // Startet wo der Playhead steht
      duration: 3, // Default 3 Sekunden
      x: 0,  // Zentrum
      y: 0,
      scale: 1,
      rotation: 0, // 🔄 Für Pinch-Rotate
      fontFamily: 'Inter',
      fontSize: 32,
      color: '#FFFFFF',
      animation: 'none'
    };
    setTextTracks(prev => [...prev, newText]);
    setSelectedTextId(newText.id);
    setIsEditingText(newText.id); // Sofort Eingabe öffnen
    console.log(`🔤 Text hinzugefügt bei ${newText.startTime.toFixed(2)}s`);
  }, []);

  // Text-Track entfernen
  const removeTextTrack = useCallback((textId) => {
    setTextTracks(prev => prev.filter(t => t.id !== textId));
    setSelectedTextId(null);
    setIsEditingText(null);
  }, []);

  // 🔤 TEXT INTERACTION (Move + Trim)
  const handleTextInteraction = useCallback((e, textId, type) => {
    e.stopPropagation();
    
    // 🎯 PRÄZISIONS-CHECK
    const target = e.currentTarget;
    if (target) {
      const rect = target.getBoundingClientRect();
      const touchX = e.touches ? e.touches[0].clientX : e.clientX;
      const touchY = e.touches ? e.touches[0].clientY : e.clientY;
      const tolerance = 10;
      if (touchX < rect.left - tolerance || touchX > rect.right + tolerance ||
          touchY < rect.top - tolerance || touchY > rect.bottom + tolerance) {
        return;
      }
    }
    
    if (navigator.vibrate) navigator.vibrate(5);
    
    const track = textTracks.find(t => t.id === textId);
    if (!track || !timelineRef.current) return;
    
    const initialEndTime = track.startTime + track.duration;
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const initialStartTime = track.startTime;
    
    if (type !== 'move') setIsTextTrimming(type);

    const onMove = (ev) => {
      if (ev.cancelable) ev.preventDefault();
      
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const rect = timelineRef.current.getBoundingClientRect();
      const scrollLeft = timelineRef.current.scrollLeft;
      const paddingLeft = window.innerWidth / 2;
      const absoluteX = clientX - rect.left + scrollLeft - paddingLeft;
      const currentSecond = absoluteX / zoom;

      setTextTracks(prev => prev.map(t => {
        if (t.id !== textId) return t;
        
        if (type === 'move') {
          const deltaX = clientX - startX;
          const deltaTime = deltaX / zoom;
          return { ...t, startTime: Math.max(0, initialStartTime + deltaTime) };
        } else if (type === 'trim-right') {
          const newDuration = currentSecond - t.startTime;
          return { ...t, duration: Math.max(0.5, newDuration) };
        } else if (type === 'trim-left') {
          const newStart = Math.max(0, currentSecond);
          return { ...t, startTime: newStart, duration: Math.max(0.5, initialEndTime - newStart) };
        }
        return t;
      }));
    };

    const onEnd = () => {
      setIsTextTrimming(null);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  }, [textTracks, zoom]);

  // 🔪 AUDIO SPLIT (am Playhead teilen)
  const splitAudio = useCallback(() => {
    if (!selectedAudioId) return;
    
    const trackIndex = audioTracks.findIndex(t => t.id === selectedAudioId);
    const track = audioTracks[trackIndex];
    if (!track) return;
    
    // Schnittpunkt relativ zum Track-Start
    const splitPoint = currentTime - track.startTime;
    
    // Nur schneiden wenn Playhead IM Track liegt
    if (splitPoint <= 0 || splitPoint >= track.duration) {
      console.log('⚠️ Playhead liegt nicht im Audio-Track');
      return;
    }
    
    // Zwei neue Teile erstellen
    const part1Id = `audio_${Date.now()}_1`;
    const part2Id = `audio_${Date.now()}_2`;
    
    const part1 = {
      ...track,
      duration: splitPoint,
      id: part1Id
    };
    const part2 = {
      ...track,
      startTime: currentTime,
      duration: track.duration - splitPoint,
      id: part2Id
    };
    
    // 🎵 WICHTIG: Audio-Elemente für beide Teile erstellen!
    const createAudioElement = (url) => {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.crossOrigin = 'anonymous';
      audio.preservesPitch = false;
      audio.src = url;
      audio.load();
      return audio;
    };
    
    // Altes Audio-Element entfernen
    if (audioRefs.current[selectedAudioId]) {
      audioRefs.current[selectedAudioId].pause();
      delete audioRefs.current[selectedAudioId];
    }
    
    // Neue Audio-Elemente erstellen
    audioRefs.current[part1Id] = createAudioElement(track.url);
    audioRefs.current[part2Id] = createAudioElement(track.url);
    
    // Alten Track durch zwei neue ersetzen
    const newTracks = [...audioTracks];
    newTracks.splice(trackIndex, 1, part1, part2);
    setAudioTracks(newTracks);
    
    // Hinteren Teil auswählen (zum schnellen Löschen)
    setSelectedAudioId(part2Id);
    
    console.log(`🔪 Audio geteilt bei ${currentTime.toFixed(2)}s`);
  }, [selectedAudioId, audioTracks, currentTime]);

  // 🗑️ AUDIO DELETE (ausgewählten Track löschen)
  const deleteSelectedAudio = useCallback(() => {
    if (!selectedAudioId) return;
    removeAudioTrack(selectedAudioId);
    console.log('🗑️ Audio gelöscht');
  }, [selectedAudioId, removeAudioTrack]);

  // Refs für stabile Playback-Werte (kein Re-Render nötig)
  const totalDurationRef = useRef(totalDuration);
  const zoomRef = useRef(zoom);
  totalDurationRef.current = totalDuration;
  zoomRef.current = zoom;

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 3. PLAYBACK ENGINE (Nur isPlaying als Dependency!)
  // ═══════════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    isPlayingRef.current = isPlaying;
    
    if (isPlaying && totalDurationRef.current > 0) {
      lastTickTime.current = performance.now();
      
      const tick = () => {
        if (!isPlayingRef.current) return;

        const now = performance.now();
        const delta = (now - lastTickTime.current) / 1000;
        lastTickTime.current = now;

        const currentTotal = totalDurationRef.current;
        const currentZoom = zoomRef.current;
        
        let nextTime = masterTimeRef.current + delta;
        
        if (nextTime >= (currentTotal - 0.02)) {
          masterTimeRef.current = 0;
          setCurrentTime(0);
          setIsPlaying(false);
          if (timelineRef.current) timelineRef.current.scrollLeft = 0;
          if (timeDisplayRef.current) {
            timeDisplayRef.current.textContent = `0.00s / ${currentTotal.toFixed(2)}s`;
          }
          if (rulerRef.current) {
            rulerRef.current.style.transform = `translate3d(${window.innerWidth / 2}px, 0, 0)`;
          }
          return;
        }
        
        masterTimeRef.current = nextTime;
        setCurrentTime(nextTime);
        
        // 🚀 Direct DOM updates
        if (timeDisplayRef.current) {
          timeDisplayRef.current.textContent = `${nextTime.toFixed(2)}s / ${currentTotal.toFixed(2)}s`;
        }
        if (rulerRef.current) {
          const rulerOffset = (window.innerWidth / 2) - (nextTime * currentZoom);
          rulerRef.current.style.transform = `translate3d(${rulerOffset}px, 0, 0)`;
        }
        if (timelineRef.current) {
          timelineRef.current.scrollLeft = nextTime * currentZoom;
        }

        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying]); // ⚡ NUR isPlaying! Alles andere aus Refs

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 4. ACTIVE CLIP DETECTION
  // ═══════════════════════════════════════════════════════════════════════════
  
  const activeClip = useMemo(() => {
    if (clipsWithTiming.length === 0) return null;
    const found = clipsWithTiming.find(c => currentTime >= c.start && currentTime < c.end);
    return found || clipsWithTiming[clipsWithTiming.length - 1];
  }, [currentTime, clipsWithTiming]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🚀 5. ZERO-LAG SCRUB HANDLER (Direct DOM, no React re-render!)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleScrub = useCallback((e) => {
    if (!timelineRef.current || totalDurationRef.current === 0) return;
    if (isPlayingRef.current) return; // Nicht scrubben während Playback
    
    const scrollPos = e.target.scrollLeft;
    const currentTotal = totalDurationRef.current;
    const currentZoom = zoomRef.current;
    const newTime = Math.max(0, Math.min(currentTotal, scrollPos / currentZoom));
    
    // Update ref (source of truth)
    masterTimeRef.current = newTime;
    
    // 🚀 DIRECT DOM UPDATE - No React re-render!
    if (timeDisplayRef.current) {
      timeDisplayRef.current.textContent = `${newTime.toFixed(2)}s / ${currentTotal.toFixed(2)}s`;
    }
    
    // 🚀 GPU-ACCELERATED Ruler update
    if (rulerRef.current) {
      const rulerOffset = (window.innerWidth / 2) - (newTime * currentZoom);
      rulerRef.current.style.transform = `translate3d(${rulerOffset}px, 0, 0)`;
    }
    
    // 🎵 THROTTLED AUDIO SEEK (max alle 50ms)
    const now = performance.now();
    if (now - lastAudioSeekRef.current > 50) {
      lastAudioSeekRef.current = now;
      // Setze Audio-Position für alle Tracks
      audioTracks.forEach(track => {
        const audio = audioRefs.current[track.id];
        if (audio) {
          const playbackOffset = newTime - track.startTime;
          const audioFileTime = playbackOffset + (track.audioOffset || 0);
          if (playbackOffset >= 0 && playbackOffset < track.duration) {
            audio.currentTime = audioFileTime;
          }
        }
      });
    }
    
    // Update React state for video preview
    setCurrentTime(newTime);
  }, [audioTracks]);

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎨 6. TRANSFORM MODE (Pan & Zoom wie CapCut)
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Hilfsfunktion zum Berechnen des Finger-Abstands (für Pinch-Zoom)
  const getDistance = (t1, t2) => {
    return Math.sqrt(Math.pow(t2.clientX - t1.clientX, 2) + Math.pow(t2.clientY - t1.clientY, 2));
  };
  
  // Hilfsfunktion zum Berechnen des Winkels (für Rotation)
  const getAngle = (t1, t2) => {
    return Math.atan2(t2.clientY - t1.clientY, t2.clientX - t1.clientX) * 180 / Math.PI;
  };

  // 🔤 TEXT TRANSFORM HANDLER (Pan + Pinch + Rotate)
  const handleTextPreviewInteraction = useCallback((e, textId) => {
    if (isPlaying) return;
    e.stopPropagation();
    e.preventDefault(); // 🔒 Verhindert Event-Durchleitung zum Bild
    selectText(textId);
    
    const text = textTracks.find(t => t.id === textId);
    if (!text) return;
    
    // Mutable state für dynamische Finger-Erkennung
    const state = {
      startX: 0,
      startY: 0,
      startDist: 0,
      startAngle: 0,
      initialX: text.x || 0,
      initialY: text.y || 0,
      startScale: text.scale || 1,
      startRotation: text.rotation || 0,
      lastTouchCount: 0
    };
    
    // Initialisiere basierend auf aktuellen Touches
    const initTouches = (touches) => {
      if (touches.length === 1) {
        state.startX = touches[0].clientX;
        state.startY = touches[0].clientY;
      } else if (touches.length === 2) {
        state.startDist = getDistance(touches[0], touches[1]);
        state.startAngle = getAngle(touches[0], touches[1]);
        // Aktuelle Werte als Startpunkt speichern
        const currentText = textTracks.find(t => t.id === textId);
        if (currentText) {
          state.startScale = currentText.scale || 1;
          state.startRotation = currentText.rotation || 0;
        }
      }
      state.lastTouchCount = touches.length;
    };
    
    initTouches(e.touches);
    
    const onMove = (moveEvent) => {
      if (moveEvent.cancelable) moveEvent.preventDefault();
      const mTouches = moveEvent.touches;
      
      // Wenn sich die Anzahl der Finger ändert, neu initialisieren
      if (mTouches.length !== state.lastTouchCount) {
        // Aktuelle Position als neuen Startpunkt speichern
        const currentText = textTracks.find(t => t.id === textId);
        if (currentText) {
          state.initialX = currentText.x || 0;
          state.initialY = currentText.y || 0;
          state.startScale = currentText.scale || 1;
          state.startRotation = currentText.rotation || 0;
        }
        initTouches(mTouches);
        return;
      }
      
      requestAnimationFrame(() => {
        setTextTracks(prev => prev.map(t => {
          if (t.id !== textId) return t;
          
          if (mTouches.length === 1) {
            // PAN: Schieben mit SNAP
            const dx = mTouches[0].clientX - state.startX;
            const dy = mTouches[0].clientY - state.startY;
            let newX = state.initialX + dx;
            let newY = state.initialY + dy;
            
            // 🧲 SNAP-TO-CENTER
            let snappedX = false;
            let snappedY = false;
            
            if (Math.abs(newX) < SNAP_THRESHOLD) {
              if (t.x !== 0 && navigator.vibrate) navigator.vibrate(10);
              newX = 0;
              snappedX = true;
            }
            if (Math.abs(newY) < SNAP_THRESHOLD) {
              if (t.y !== 0 && navigator.vibrate) navigator.vibrate(10);
              newY = 0;
              snappedY = true;
            }
            
            setSnapLines({ x: snappedX, y: snappedY });
            return { ...t, x: newX, y: newY };
          } else if (mTouches.length === 2 && state.startDist > 0) {
            // PINCH & ROTATE
            const currentDist = getDistance(mTouches[0], mTouches[1]);
            const currentAngle = getAngle(mTouches[0], mTouches[1]);
            const scaleDiff = currentDist / state.startDist;
            const angleDiff = currentAngle - state.startAngle;
            return { 
              ...t, 
              scale: Math.max(0.2, Math.min(5, state.startScale * scaleDiff)),
              rotation: state.startRotation + angleDiff 
            };
          }
          return t;
        }));
      });
    };
    
    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      setSnapLines({ x: false, y: false }); // 🧲 Reset Snap-Lines
    };
    
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }, [isPlaying, textTracks, selectText]);

  // 🖼️ CLIP INTERACTION HANDLER - DIREKTE DOM MANIPULATION für flüssiges Drag!
  const handleClipInteraction = useCallback((e, clipId) => {
    if (isPlaying) return;
    if (selectedTextId) return; // 🔒 Nicht reagieren wenn Text selektiert
    selectClip(clipId);
    
    const clip = clips.find(c => c.id === clipId);
    if (!clip) return;
    
    // Finde das Clip-Element per data-attribute
    const element = document.querySelector(`[data-clip-id="${clipId}"]`);
    if (!element) return;
    
    const state = {
      startX: 0,
      startY: 0,
      startDist: 0,
      currentX: clip.x || 0,
      currentY: clip.y || 0,
      currentScale: clip.scale || 1,
      lastTouchCount: 0
    };
    
    const initTouches = (touches) => {
      if (touches.length === 1) {
        state.startX = touches[0].clientX;
        state.startY = touches[0].clientY;
      } else if (touches.length === 2) {
        state.startDist = getDistance(touches[0], touches[1]);
      }
      state.lastTouchCount = touches.length;
    };
    
    initTouches(e.touches);
    
    const onMove = (moveEvent) => {
      if (moveEvent.cancelable) moveEvent.preventDefault();
      const mTouches = moveEvent.touches;
      
      if (mTouches.length !== state.lastTouchCount) {
        initTouches(mTouches);
        return;
      }
      
      if (mTouches.length === 1) {
        const dx = mTouches[0].clientX - state.startX;
        const dy = mTouches[0].clientY - state.startY;
        let newX = (clip.x || 0) + dx;
        let newY = (clip.y || 0) + dy;
        
        // 🧲 SNAP-TO-CENTER LOGIK
        let snappedX = false;
        let snappedY = false;
        
        if (Math.abs(newX) < SNAP_THRESHOLD) {
          if (state.currentX !== 0 && navigator.vibrate) navigator.vibrate(10);
          newX = 0;
          snappedX = true;
        }
        if (Math.abs(newY) < SNAP_THRESHOLD) {
          if (state.currentY !== 0 && navigator.vibrate) navigator.vibrate(10);
          newY = 0;
          snappedY = true;
        }
        
        setSnapLines({ x: snappedX, y: snappedY });
        state.currentX = newX;
        state.currentY = newY;
      } else if (mTouches.length === 2 && state.startDist > 0) {
        const currentDist = getDistance(mTouches[0], mTouches[1]);
        const scaleDiff = currentDist / state.startDist;
        state.currentScale = Math.max(0.3, Math.min(4, (clip.scale || 1) * scaleDiff));
      }
      // Direkt React State updaten (zuverlässiger als DOM-Manipulation)
      setClips(prev => prev.map(c => 
        c.id === clipId ? { ...c, x: state.currentX, y: state.currentY, scale: state.currentScale } : c
      ));
    };
    
    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };
    
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }, [isPlaying, clips, selectClip, selectedTextId]);

  // Update Clip Transform (x, y, scale)
  const updateClipTransform = useCallback((clipId, transform) => {
    setClips(prev => prev.map(c => 
      c.id === clipId ? { ...c, ...transform } : c
    ));
  }, []);

  // Touch Handler für Preview (Pan & Zoom)
  const handlePreviewTouch = useCallback((e) => {
    if (!selectedClipId || isPlaying) return; // Nur im Pause-Modus und bei Selektion
    
    const touches = e.touches;
    const activeClipData = clips.find(c => c.id === selectedClipId);
    if (!activeClipData) return;

    if (e.type === 'touchstart') {
      if (touches.length === 1) {
        // Startpunkt für 1-Finger-Pan
        setTouchStart({
          x: touches[0].clientX,
          y: touches[0].clientY,
          initialX: activeClipData.x || 0,
          initialY: activeClipData.y || 0
        });
      } else if (touches.length === 2) {
        // Start-Abstand für 2-Finger-Zoom
        setTouchStart({
          distance: getDistance(touches[0], touches[1]),
          initialScale: activeClipData.scale || 1
        });
      }
    } else if (e.type === 'touchmove') {
      if (touches.length === 1 && touchStart.x !== undefined) {
        // EIN FINGER: Schieben (Pan)
        const dx = touches[0].clientX - touchStart.x;
        const dy = touches[0].clientY - touchStart.y;
        updateClipTransform(selectedClipId, {
          x: touchStart.initialX + dx,
          y: touchStart.initialY + dy
        });
      } else if (touches.length === 2 && touchStart.distance) {
        // ZWEI FINGER: Zoomen (Pinch)
        const currentDist = getDistance(touches[0], touches[1]);
        const scaleFactor = currentDist / touchStart.distance;
        updateClipTransform(selectedClipId, {
          scale: Math.max(0.5, Math.min(5, touchStart.initialScale * scaleFactor))
        });
      }
    }
  }, [selectedClipId, isPlaying, clips, touchStart, updateClipTransform]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ✂️ TRIM HANDLER
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleTrim = (e, direction, clipId) => {
    e.stopPropagation();
    setIsTrimming(direction);
    
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const originalClip = clips.find(c => c.id === clipId);
    const originalDuration = originalClip?.duration || 3;
    const currentZoom = zoomRef.current; // Aktueller Zoom-Wert
    
    // 🎬 Maximale Dauer für Videos (Bilder können beliebig lang sein)
    const maxDuration = originalClip?.videoUrl // Animierte Bilder haben videoUrl 
      ? (originalClip.maxDuration || originalClip.duration || 5) // Video: max = echte Länge
      : 999; // Bilder: unbegrenzt

    // Berechne Clip-Startzeit (Summe aller vorherigen Clips)
    const clipIndex = clips.findIndex(c => c.id === clipId);
    const clipStartTime = clips.slice(0, clipIndex).reduce((acc, c) => acc + (c.duration || 3), 0);

    const onMove = (moveEvent) => {
      if (moveEvent.cancelable) moveEvent.preventDefault();
      const currentX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const deltaX = currentX - startX;
      const deltaTime = deltaX / currentZoom; // Pixel in Sekunden

      // 1. Berechne die neue Dauer für diesen Clip
      let newDuration = originalDuration;
      let newTrimStart = 0; // Für Links-Trim
      if (direction === 'right') {
        // 🎬 Videos: Nicht länger als maxDuration!
        newDuration = Math.min(maxDuration, Math.max(0.5, originalDuration + deltaTime));
      } else if (direction === 'left') {
        newDuration = Math.max(0.5, originalDuration - deltaTime);
        newTrimStart = Math.max(0, -deltaTime); // Wie viel vom Anfang abgeschnitten
      }

      // 2. 🚀 BLITZSCHNELLE BERECHNUNG DER NEUEN GESAMTZEIT (ohne State-Verzögerung)
      const tempTotalDuration = clips.reduce((acc, c) => {
        if (c.id === clipId) return acc + newDuration;
        return acc + (c.duration || 3);
      }, 0);

      // 3. 🚀 DIREKTES UPDATE DER ANZEIGE (Butterweich)
      if (timeDisplayRef.current) {
        timeDisplayRef.current.textContent = `${masterTimeRef.current.toFixed(2)}s / ${tempTotalDuration.toFixed(2)}s`;
      }

      // 4. Den State nur für die visuelle Breite der Clips aktualisieren
      setClips(prev => prev.map(c => 
        c.id === clipId ? { ...c, duration: newDuration } : c
      ));

      // 5. 🎬 LIVE PREVIEW: Zeige das Frame an der Trim-Position!
      if (direction === 'right') {
        // Rechts trimmen → zeige das Ende des Clips
        const previewTime = clipStartTime + newDuration - 0.1; // Kurz vor Ende
        setCurrentTime(Math.max(0, previewTime));
        masterTimeRef.current = Math.max(0, previewTime);
      } else if (direction === 'left') {
        // Links trimmen → zeige den Anfang des Clips (nach dem Trim)
        const previewTime = clipStartTime; // Clip-Start bleibt gleich (vorherige Clips unverändert)
        setCurrentTime(Math.max(0, previewTime));
        masterTimeRef.current = Math.max(0, previewTime);
      }

      // 6. 🚀 Beim Links-Trimmen: Scroll-Position anpassen (kein "Hüpfen")
      if (direction === 'left' && timelineRef.current) {
        const newScrollPos = masterTimeRef.current * currentZoom;
        timelineRef.current.scrollLeft = newScrollPos;
      }
    };

    const onEnd = () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      setIsTrimming(null);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
  };

  if (!isOpen) return null;

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="fixed inset-0 z-[9999] bg-black flex flex-col h-dvh font-sans select-none overflow-hidden text-white">
      
      {/* LOADING SCREEN */}
      {showLoading && (
        <div className="absolute inset-0 z-[10000] bg-black flex flex-col items-center justify-center">
          <div className="relative w-32 h-32 flex items-center justify-center">
            <svg className="w-full h-full -rotate-90">
              <circle cx="64" cy="64" r="50" fill="none" stroke="#222" strokeWidth="8" />
              <circle 
                cx="64" cy="64" r="50" fill="none" stroke="#10B981" strokeWidth="8" 
                strokeDasharray="314" 
                strokeDashoffset={314 - (314 * loadProgress) / 100} 
                strokeLinecap="round" 
                className="transition-all duration-300" 
              />
            </svg>
            <span className="absolute text-2xl font-bold">{loadProgress}%</span>
          </div>
          <h2 className="mt-8 text-xl font-medium">Frames werden geladen...</h2>
          <p className="text-gray-500 text-sm mt-2">Für butterweichen Scrub 🧈</p>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 🎬 FULLSCREEN OVERLAY (Separates Layer - Perfect 9:16 Sync) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 🎬 FULLSCREEN SAFE-CANVAS (Video kann nie über Controls ragen) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {isFullscreen && (
        <div className="fixed inset-0 z-[1000] bg-black flex flex-col items-center justify-between py-6">
          
          {/* 1. HEADER: ZURÜCK-BUTTON (Oben, immer sichtbar) */}
          <div className="w-full px-6 flex justify-start z-[1100]">
            <button 
              onClick={() => setIsFullscreen(false)}
              className="p-3 bg-zinc-900/80 backdrop-blur-md rounded-full border border-white/10 active:scale-90 transition-all"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
          </div>

          {/* 2. DAS SPIELFELD (9:16 Safe Frame - overflow:hidden schneidet ab) */}
          <div 
            className="relative bg-zinc-950 overflow-hidden shadow-2xl"
            style={{
              aspectRatio: '9/16',
              height: '75vh',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 0 40px rgba(0,0,0,1)'
            }}
          >
            {/* 🧲 SNAP-LINIEN FULLSCREEN */}
            {snapLines.x && (
              <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-cyan-400 z-[200] pointer-events-none" 
                   style={{ boxShadow: '0 0 8px 2px rgba(34, 211, 238, 0.6)' }} />
            )}
            {snapLines.y && (
              <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-cyan-400 z-[200] pointer-events-none" 
                   style={{ boxShadow: '0 0 8px 2px rgba(34, 211, 238, 0.6)' }} />
            )}

            {/* EBENE 1: DAS BILD (Hintergrund) */}
            <div 
              className="absolute inset-0 z-10 touch-none flex items-center justify-center"
              onTouchStart={(e) => {
                const activeClip = clipsWithTiming.find(c => currentTime >= c.start && currentTime < c.end);
                if (activeClip) handleClipInteraction(e, activeClip.id);
              }}
            >
              {/* 🎬 UNIVERSAL WRAPPER - FULLSCREEN */}
              {clipsWithTiming.map((clip) => {
                const isActive = currentTime >= clip.start && currentTime < clip.end;
                const hasVideo = !!clip.videoUrl; // Animierte Bilder haben videoUrl aber type='image'
                
                return (
                  <div
                    key={clip.id}
                    className="absolute inset-0"
                    style={{ 
                      zIndex: isActive ? 10 : 1,
                      visibility: isActive ? 'visible' : 'hidden',
                    }}
                  >
                    {/* 🔒 UNIVERSAL WRAPPER - TRANSFORM CONTAINER */}
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{
                        transform: `translate3d(${clip.x || 0}px, ${clip.y || 0}px, 0) scale(${clip.scale || 1})`,
                        transformOrigin: 'center center',
                        willChange: 'transform',
                      }}
                    >
                      {/* 🎬 CAPCUT-STYLE: EIN ELEMENT PRO CLIP */}
                      {hasVideo ? (
                        <video
                          data-clip-video={clip.id}
                          ref={(el) => {
                            if (el && isActive) {
                              const videoTime = Math.max(0, Math.min(currentTime - clip.start, clip.duration - 0.01));
                              if (Math.abs(el.currentTime - videoTime) > 0.05) {
                                el.currentTime = videoTime;
                              }
                              if (isPlaying && el.paused && el.readyState >= 2) {
                                el.play().catch(() => {});
                              } else if (!isPlaying && !el.paused) {
                                el.pause();
                              }
                            }
                          }}
                          src={clip.videoUrl}
                          poster={clip.thumbnail || clip.url}
                          className="max-w-full max-h-full object-contain pointer-events-none"
                          muted
                          playsInline
                          webkit-playsinline="true"
                          preload="auto"
                          draggable={false}
                        />
                      ) : (
                        <img
                          src={clip.thumbnail || clip.url}
                          alt=""
                          draggable={false}
                          className="max-w-full max-h-full object-contain pointer-events-none"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* EBENE 2: DER TEXT (Vordergrund - stopPropagation isoliert vom Bild) */}
            <div className="absolute inset-0 z-20 pointer-events-none">
              {textTracks.map((txt) => {
                const isActive = currentTime >= txt.startTime && currentTime < (txt.startTime + txt.duration);
                if (!isActive) return null;
                return (
                  <div
                    key={txt.id}
                    className={`absolute pointer-events-auto select-none touch-none ${
                      selectedTextId === txt.id ? 'ring-1 ring-cyan-500/30' : ''
                    }`}
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `translate3d(calc(-50% + ${txt.x || 0}px), calc(-50% + ${txt.y || 0}px), 0) rotate(${txt.rotation || 0}deg) scale(${txt.scale || 1})`,
                      transformOrigin: 'center center',
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      handleTextPreviewInteraction(e, txt.id);
                    }}
                    onDoubleClick={() => setIsEditingText(txt.id)}
                  >
                    <span 
                      className="text-white font-bold whitespace-nowrap drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
                      style={{ fontSize: `${txt.fontSize || 32}px`, fontFamily: txt.fontFamily || 'Inter' }}
                    >
                      {txt.content || "TEXT HIER"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 3. FOOTER: PLAY-CONTROLS (Unten, niemals überdeckt) */}
          <div className="w-full flex justify-center pb-4 z-[1100]">
            <button 
              onClick={() => {
                // 🔊 MOBILE: Alle Audio-Elemente DIREKT hier starten (User-Gesture!)
                if (!isPlaying) {
                  // AudioContext resumen
                  if (audioContextRef.current?.state === 'suspended') {
                    audioContextRef.current.resume();
                  }
                  // ALLE Audio-Tracks direkt starten
                  audioTracks.forEach(track => {
                    const audio = audioRefs.current[track.id];
                    if (audio) {
                      audio.currentTime = currentTime; // Sync mit Timeline
                      audio.play().catch(e => console.log('Audio blocked:', e.name));
                    }
                  });
                } else {
                  // Pause: Alle stoppen
                  Object.values(audioRefs.current).forEach(audio => {
                    if (audio) audio.pause();
                  });
                }
                setIsPlaying(!isPlaying);
              }}
              className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl active:scale-90 transition-all"
            >
              {isPlaying ? <Pause size={32} className="text-black" /> : <Play size={32} className="text-black ml-1" />}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* BEREICH 1: WERKSTATT - 9:16 CANVAS MIT APPLE-STYLE SIDE-TOOLS */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="relative h-[58vh] w-full bg-[#0a0a0a] p-4 flex items-center justify-center">
        {/* Header - Nur Zurück-Button */}
        <button 
          onClick={() => { stopAllAudio(); onClose(); }} 
          className="absolute top-4 left-4 z-30 p-2 bg-black/40 backdrop-blur-md rounded-full"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        {/* 🍏 APPLE-STYLE SIDE-BAR (Rechts neben dem Video) */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-[600]">
          {/* AUDIO BUTTON */}
          <button 
            onClick={() => setShowMusicLibrary(true)}
            className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl active:scale-90 transition-all"
          >
            <Music size={16} className={`drop-shadow-md ${audioTracks.length > 0 ? 'text-blue-400' : 'text-white'}`} />
          </button>

          {/* TEXT BUTTON */}
          <button 
            onClick={addTextTrack}
            className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl active:scale-90 transition-all"
          >
            <Type size={16} className={`drop-shadow-md ${textTracks.length > 0 ? 'text-orange-400' : 'text-white'}`} />
          </button>

          {/* 🪄 ANIMIEREN BUTTON (immer sichtbar) */}
          <button 
            onClick={() => {
              if (!selectedClipId) {
                alert('Wähle zuerst ein Bild aus!');
                return;
              }
              const clip = clips.find(c => c.id === selectedClipId);
              if (clip?.type === 'video') {
                alert('Dieses Bild ist bereits ein Video!');
                return;
              }
              animateClip(selectedClipId);
              if (navigator.vibrate) navigator.vibrate(15);
            }}
            className="w-9 h-9 flex items-center justify-center bg-purple-500/80 backdrop-blur-xl border border-purple-400/30 rounded-full shadow-2xl active:scale-90 transition-all hover:bg-purple-400/80"
          >
            <Sparkles size={16} className="text-white drop-shadow-md" />
          </button>

          {/* ✨ MAGIC BUTTON - Öffnet Magic Create (2 Varianten) - NUR für Master-Admin */}
          {isMasterAdmin && (
            <button
              onClick={() => {
                setShowMagicCreate(true);
                if (navigator.vibrate) navigator.vibrate(15);
              }}
              className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-emerald-500/80 to-teal-500/80 backdrop-blur-xl border border-emerald-400/30 rounded-full shadow-2xl active:scale-90 transition-all hover:from-emerald-400/80 hover:to-teal-400/80"
              title="Magic - 2 Varianten erstellen"
            >
              <Wand2 size={16} className="text-white drop-shadow-md" />
            </button>
          )}

          {/* 🗑️ AUDIO LÖSCHEN BUTTON (erscheint bei ausgewähltem Audio) */}
          {selectedAudioId && (
            <button 
              onClick={() => {
                if (window.confirm('Audio-Track wirklich löschen?')) {
                  stopAllAudio();
                  setAudioTracks(prev => prev.filter(t => t.id !== selectedAudioId));
                  setSelectedAudioId(null);
                  if (navigator.vibrate) navigator.vibrate([20, 50, 20]);
                }
              }}
              className="w-9 h-9 flex items-center justify-center bg-red-500/80 backdrop-blur-xl border border-red-400/30 rounded-full shadow-2xl active:scale-90 transition-all hover:bg-red-400/80"
            >
              <Trash2 size={16} className="text-white drop-shadow-md" />
            </button>
          )}

          {/* FULLSCREEN BUTTON */}
          <button 
            onClick={(e) => { e.stopPropagation(); setIsFullscreen(true); }}
            className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl active:scale-90 transition-all"
          >
            <Maximize2 size={16} className="text-white drop-shadow-md" />
          </button>

          {/* 💾 SPEICHERN (bleibt in Werkstatt) */}
          <button 
            onClick={() => {
              // Projekt wird automatisch gespeichert (Auto-Save läuft sowieso)
              if (navigator.vibrate) navigator.vibrate([10, 30, 10]);
              
              // Eleganter Toast mit Render-Hinweis
              const toast = document.createElement('div');
              toast.textContent = '✓ Gespeichert! Video wird erstellt...';
              toast.className = 'fixed top-20 left-1/2 -translate-x-1/2 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-xl z-[9999] animate-pulse';
              document.body.appendChild(toast);
              setTimeout(() => {
                toast.style.transition = 'opacity 0.3s';
                toast.style.opacity = '0';
                setTimeout(() => toast.remove(), 300);
              }, 1500);
            }}
            className="w-9 h-9 flex items-center justify-center bg-emerald-500/80 backdrop-blur-xl border border-emerald-400/30 rounded-full shadow-2xl active:scale-90 transition-all"
          >
            <Check size={18} className="text-white drop-shadow-md" />
          </button>
        </div>

        {/* 📐 DER FESTE 9:16 RAHMEN (Unantastbarer Käfig - nie verformen!) */}
        <div 
          className="relative bg-black overflow-hidden shadow-2xl"
          style={{ 
            aspectRatio: '9 / 16',
            height: '100%',
            maxHeight: '100%',
            border: '0.5px solid rgba(255,255,255,0.2)', 
            borderRadius: '4px' 
          }}
        >
          
          {/* Kein separater Interaktions-Layer - Touch direkt auf Clips! */}

          {/* 🧲 SNAP-TO-CENTER HILFSLINIEN */}
          {snapLines.x && (
            <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-cyan-400 z-[200] pointer-events-none" 
                 style={{ boxShadow: '0 0 8px 2px rgba(34, 211, 238, 0.6)' }} />
          )}
          {snapLines.y && (
            <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-cyan-400 z-[200] pointer-events-none" 
                 style={{ boxShadow: '0 0 8px 2px rgba(34, 211, 238, 0.6)' }} />
          )}

          {/* 🎬 UNIVERSAL WRAPPER SYSTEM - ZERO GLITCHES */}
          {clipsWithTiming.map((clip) => {
            const isActive = currentTime >= clip.start && currentTime < clip.end;
            const isSelected = clip.id === selectedClipId;
            const hasVideo = !!clip.videoUrl; // Animierte Bilder haben videoUrl aber type='image'
            
            return (
              <div
                key={clip.id}
                className="absolute inset-0"
                style={{ 
                  zIndex: isActive ? 10 : 1,
                  visibility: isActive ? 'visible' : 'hidden',
                  pointerEvents: isActive ? 'auto' : 'none',
                }}
              >
                {/* 🔒 UNIVERSAL WRAPPER - NUR DIESER WIRD TRANSFORMIERT */}
                <div
                  data-clip-id={clip.id}
                  className="absolute inset-0 flex items-center justify-center"
                  style={{
                    transform: `translate3d(${clip.x || 0}px, ${clip.y || 0}px, 0) scale(${clip.scale || 1})`,
                    transformOrigin: 'center center',
                    willChange: 'transform',
                    touchAction: 'none',
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    if (!isPlaying) handleClipInteraction(e, clip.id);
                  }}
                  onClick={() => !isPlaying && selectClip(clip.id)}
                >
                  {/* 🎨 ECKEN-MARKER (nur bei Selektion) */}
                  {isSelected && !isPlaying && (
                    <>
                      <div className="absolute -top-1 -left-1 w-3 h-3 bg-white border-2 border-cyan-500 rounded-full z-30 shadow-md" style={{top: '10%', left: '10%'}} />
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-white border-2 border-cyan-500 rounded-full z-30 shadow-md" style={{top: '10%', right: '10%'}} />
                      <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-white border-2 border-cyan-500 rounded-full z-30 shadow-md" style={{bottom: '10%', left: '10%'}} />
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 border-cyan-500 rounded-full z-30 shadow-md" style={{bottom: '10%', right: '10%'}} />
                    </>
                  )}
                  
                  {/* Selektion-Outline */}
                  {isSelected && !isPlaying && (
                    <div className="absolute inset-[10%] border-2 border-cyan-500 rounded pointer-events-none z-20" />
                  )}

                  {/* 🎬 CAPCUT-STYLE: EIN ELEMENT PRO CLIP */}
                  {hasVideo ? (
                    <video
                      data-clip-video={clip.id}
                      ref={(el) => {
                        if (el && isActive) {
                          // IMMER currentTime synchron halten (auch bei Pause/Scrub!)
                          const videoTime = Math.max(0, Math.min(currentTime - clip.start, clip.duration - 0.01));
                          if (Math.abs(el.currentTime - videoTime) > 0.05) {
                            el.currentTime = videoTime;
                          }
                          // Play/Pause steuern
                          if (isPlaying && el.paused && el.readyState >= 2) {
                            el.play().catch(() => {});
                          } else if (!isPlaying && !el.paused) {
                            el.pause();
                          }
                        }
                      }}
                      src={clip.videoUrl}
                      poster={clip.thumbnail || clip.url}
                      className="max-w-full max-h-full object-contain pointer-events-none"
                      muted
                      playsInline
                      webkit-playsinline="true"
                      preload="auto"
                      draggable={false}
                    />
                  ) : (
                    <img
                      src={clip.thumbnail || clip.url}
                      alt=""
                      draggable={false}
                      className="max-w-full max-h-full object-contain pointer-events-none"
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* 🔤 TEXT OVERLAY (Unified Gesture Transform - Pan/Pinch/Rotate) */}
          <div className="absolute inset-0 pointer-events-none z-[110]">
            {textTracks.map((txt) => {
              const isActive = currentTime >= txt.startTime && currentTime < (txt.startTime + txt.duration);
              if (!isActive) return null;
              const isSelected = selectedTextId === txt.id;
              
              return (
                <div
                  key={txt.id}
                  className="absolute pointer-events-auto select-none touch-none cursor-grab active:cursor-grabbing"
                  style={{
                    left: '50%',
                    top: '50%',
                    // GPU-beschleunigt mit translate3d + rotate + scale
                    transform: `translate3d(calc(-50% + ${txt.x || 0}px), calc(-50% + ${txt.y || 0}px), 0) rotate(${txt.rotation || 0}deg) scale(${txt.scale || 1})`,
                    transformOrigin: 'center center',
                  }}
                  onTouchStart={(e) => handleTextPreviewInteraction(e, txt.id)}
                  onMouseDown={(e) => {
                    // Desktop Drag Support
                    if (e.button !== 0) return; // Nur linke Maustaste
                    e.preventDefault();
                    selectText(txt.id);
                    
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const startTxtX = txt.x || 0;
                    const startTxtY = txt.y || 0;
                    
                    const handleMouseMove = (moveEvent) => {
                      const dx = moveEvent.clientX - startX;
                      const dy = moveEvent.clientY - startY;
                      setTextTracks(prev => prev.map(t => 
                        t.id === txt.id ? { ...t, x: startTxtX + dx, y: startTxtY + dy } : t
                      ));
                    };
                    
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Doppelklick öffnet Edit-Feld
                    if (e.detail === 2) {
                      setIsEditingText(txt.id);
                    } else {
                      selectText(txt.id);
                    }
                  }}
                >
                  {/* 🗑️ MINI MÜLLEIMER (erscheint bei Selektion) - FIXIERT */}
                  {isSelected && !isPlaying && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeTextTrack(txt.id);
                        if (navigator.vibrate) navigator.vibrate(15);
                      }}
                      className="absolute w-7 h-7 bg-red-500/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg active:scale-90 z-50"
                      style={{
                        top: '-32px',
                        left: '50%',
                        marginLeft: '-14px', // halbe Breite für echte Zentrierung
                        pointerEvents: 'auto'
                      }}
                    >
                      <Trash2 size={14} className="text-white" />
                    </button>
                  )}
                  
                  {/* TEXT MIT HAUCHDÜNNEM RAHMEN BEI SELEKTION */}
                  <span 
                    className="text-white font-bold whitespace-nowrap drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] outline-none px-2 py-1"
                    style={{
                      fontFamily: txt.fontFamily || 'Inter',
                      fontSize: `${txt.fontSize || 32}px`,
                      color: txt.color || '#FFFFFF',
                      textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                      // Hauchdünner Rahmen bei Selektion
                      border: isSelected && !isPlaying ? '0.5px solid rgba(255,255,255,0.6)' : 'none',
                      borderRadius: '4px',
                    }}
                  >
                    {txt.content || "Text"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Play Button Overlay (außerhalb des Canvas) */}
        {!isPlaying && !selectedClipId && (
          <button 
            onClick={() => setIsPlaying(true)}
            className="absolute inset-0 flex items-center justify-center z-25 pointer-events-auto"
          >
            <div className="w-16 h-16 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center">
              <Play fill="white" className="w-8 h-8 ml-1" />
            </div>
          </button>
        )}
        
{/* Transform Mode Hinweis entfernt - Clean UI */}
        
      </div>

      {/* 🔤 INLINE TEXT-INPUT BAR (CapCut-Style unter dem Video) */}
      {isEditingText && (
        <div className="flex items-center gap-3 px-4 py-3 bg-zinc-900 border-t border-white/10">
          <Type size={18} className="text-cyan-400 flex-shrink-0" />
          <input
            autoFocus
            className="flex-1 bg-transparent text-white text-lg font-medium outline-none placeholder-zinc-500"
            placeholder="Text eingeben..."
            value={textTracks.find(t => t.id === isEditingText)?.content || ""}
            onChange={(e) => {
              const val = e.target.value;
              setTextTracks(prev => prev.map(t => 
                t.id === isEditingText ? { ...t, content: val } : t
              ));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setIsEditingText(null);
            }}
          />
          <button
            onClick={() => setIsEditingText(null)}
            className="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center active:scale-95 transition-transform"
          >
            <Check size={20} className="text-black" />
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* BEREICH 2: TIMELINE MIT ZERO-LAG */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-zinc-950 border-t border-white/10 relative z-30">
        
        {/* A. 🚀 ZERO-LAG Zeit-Anzeige (Direct DOM via Ref) */}
        <div className="flex items-center justify-between px-4 py-2 bg-black/80 border-b border-white/5">
          <span 
            ref={timeDisplayRef}
            className="text-[10px] font-mono text-cyan-400"
          >
            {currentTime.toFixed(2)}s / {totalDuration.toFixed(2)}s
          </span>
          <button 
            onClick={() => setIsPlaying(!isPlaying)} 
            className="w-8 h-8 bg-white rounded-full flex items-center justify-center"
          >
            {isPlaying ? (
              <Pause size={16} fill="black" />
            ) : (
              <Play size={16} fill="black" className="ml-0.5" />
            )}
          </button>
          <button onClick={() => setIsMuted(!isMuted)} className="text-gray-400">
            <Volume2 className={`w-5 h-5 ${isMuted ? 'opacity-50' : ''}`} />
          </button>
        </div>

        {/* B. 📏 GPU-ACCELERATED Ruler */}
        <div className="relative w-full h-8 bg-black/40 overflow-hidden border-b border-white/5">
          <div className="absolute left-1/2 top-0 bottom-0 w-[1px] bg-cyan-400 z-10 pointer-events-none" />
          
          <div 
            ref={rulerRef}
            className="flex h-full items-start pt-1"
            style={{ 
              transform: `translate3d(${(typeof window !== 'undefined' ? window.innerWidth / 2 : 200) - (currentTime * zoom)}px, 0, 0)`,
              willChange: 'transform',
              backfaceVisibility: 'hidden'
            }}
          >
            {renderRuler()}
          </div>
        </div>

        {/* C. 🚀 MULTI-TRACK TIMELINE (CapCut-Style) */}
        <div className="relative w-full py-2 pb-4 bg-[#0c0c0c]">
          {/* Mittelstrich mit Glow - durchgehend für alle Spuren */}
          <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-white z-[100] pointer-events-none shadow-[0_0_10px_rgba(255,255,255,0.5)]" />

          {/* Scroll-Container - ALLE SPUREN SCROLLEN SYNCHRON */}
          <div
            ref={timelineRef}
            className="overflow-x-auto scrollbar-hide outline-none"
            style={{ 
              touchAction: 'pan-x',
              scrollBehavior: 'auto',
              WebkitOverflowScrolling: 'touch',
              willChange: 'scroll-position',
              overscrollBehavior: 'none',
              minHeight: '180px', // 🎯 EXTRA großer Touch-Bereich (für Audio-Track)
              paddingTop: '15px', // Oberer Greif-Bereich
              paddingBottom: '10px', // Unterer Greif-Bereich
              cursor: 'grab',
            }}
            onScroll={handleScrub}
            onTouchStart={() => {
              setIsScrubbing(true);
              // 🎬 Merke ob Video lief und pausiere
              wasPlayingBeforeScrubRef.current = isPlaying;
              if (isPlaying) setIsPlaying(false);
            }}
            onTouchEnd={() => {
              setIsScrubbing(false);
              // 🎬 Starte Video wieder wenn es vorher lief
              if (wasPlayingBeforeScrubRef.current) {
                setIsPlaying(true);
              }
            }}
          >
            {/* Horizontaler Content-Container */}
            <div className="flex">
              {/* Start-Padding */}
              <div style={{ minWidth: '50vw' }} className="flex-shrink-0" />

              {/* ALLE SPUREN STACKEN HIER UNTEREINANDER */}
              <div className="flex flex-col gap-1.5 pb-2">
                
                {/* ═══ SPUR 1: BILDER ═══ */}
                <div 
                  className="flex items-center h-10 bg-zinc-900/40 rounded-sm overflow-visible"
                  style={{ transform: 'translate3d(0,0,0)', backfaceVisibility: 'hidden' }}
                >
                  {clipsWithTiming.map((clip) => {
                    const isSelected = clip.id === selectedClipId;
                    const isActive = currentTime >= clip.start && currentTime < clip.end;
                    const clipWidthPx = (clip.duration || 3) * zoom;

                    return (
                      <div
                        key={clip.id}
                        onClick={(e) => { if (!isScrubbing) { e.stopPropagation(); selectClip(clip.id); } }}
                        className={`relative h-full border-r border-black/20 overflow-hidden flex-shrink-0 ${
                          isSelected ? 'ring-2 ring-white z-50' : isActive ? 'opacity-100' : 'opacity-50'
                        }`}
                        style={{ width: `${clipWidthPx}px`, minWidth: '20px' }}
                      >
                        {/* Thumbnail - Wiederholt sich wie bei CapCut */}
                        <div 
                          className="absolute inset-0 w-full h-full pointer-events-none"
                          style={{
                            backgroundImage: `url(${clip.thumbnail || clip.url})`,
                            backgroundSize: 'auto 100%',
                            backgroundRepeat: 'repeat-x',
                            backgroundPosition: 'left center',
                          }}
                        />

                        {/* TRIMM-HANDLES */}
                        {isSelected && !clip.isGenerating && (
                          <>
                            <div
                              onTouchStart={(e) => handleTrim(e, 'left', clip.id)}
                              onMouseDown={(e) => handleTrim(e, 'left', clip.id)}
                              className="absolute left-0 top-0 bottom-0 w-6 bg-white rounded-l-md z-[70] flex items-center justify-center cursor-ew-resize shadow-lg pointer-events-auto"
                            >
                              <div className="w-0.5 h-6 bg-zinc-400" />
                            </div>
                            <div
                              onTouchStart={(e) => handleTrim(e, 'right', clip.id)}
                              onMouseDown={(e) => handleTrim(e, 'right', clip.id)}
                              className="absolute right-0 top-0 bottom-0 w-6 bg-white rounded-r-md z-[70] flex items-center justify-center cursor-ew-resize shadow-lg pointer-events-auto"
                            >
                              <div className="w-0.5 h-6 bg-zinc-400" />
                            </div>
                            <div className={`absolute -top-8 left-1/2 -translate-x-1/2 bg-cyan-500 text-black text-[10px] font-black px-2 py-0.5 rounded shadow-xl ${isTrimming ? 'animate-pulse scale-110' : ''}`}>
                              {(clip.duration || 3).toFixed(2)}s
                            </div>
                          </>
                        )}
                        
                        {/* 🪄 GENERATING OVERLAY (Shimmer + Progress) */}
                        {clip.isGenerating && (
                          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center z-[80] backdrop-blur-[1px]">
                            {/* Shimmer Gradient Animation */}
                            <div 
                              className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/30 to-transparent animate-pulse"
                              style={{ 
                                animation: 'shimmer 1.5s infinite',
                                backgroundSize: '200% 100%'
                              }}
                            />
                            {/* Sparkle Icon */}
                            <Sparkles size={14} className="text-purple-400 animate-spin mb-0.5" />
                            {/* Progress Percent */}
                            <span className="text-[10px] font-black text-white">
                              {clip.generationProgress || 0}%
                            </span>
                            {/* Progress Bar */}
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50">
                              <div 
                                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                                style={{ width: `${clip.generationProgress || 0}%` }}
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* 🎬 VIDEO BADGE (wenn Clip jetzt ein Video ist) */}
                        {clip.type === 'video' && (
                          <div className="absolute top-0.5 right-0.5 bg-purple-500/90 text-white text-[7px] font-black px-1 rounded z-[60]">
                            🎬
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ═══ SPUR 2: TEXT (ORANGE) ═══ */}
                <div
                  className="relative h-4 bg-zinc-900/10 rounded-sm overflow-visible pointer-events-none"
                  style={{ 
                    width: `${totalDuration * zoom}px`, 
                    minWidth: '100px',
                    flexShrink: 0
                  }}
                >
                  {textTracks.length > 0 ? (
                    textTracks.map(txt => {
                      const isSelected = selectedTextId === txt.id;
                      return (
                        <div
                          key={txt.id}
                          onMouseDown={(e) => { if (!isSelected) return; handleTextInteraction(e, txt.id, 'move'); }}
                          onTouchStart={(e) => { if (!isSelected) return; handleTextInteraction(e, txt.id, 'move'); }}
                          onClick={(e) => { e.stopPropagation(); selectText(txt.id); }}
                          className={`absolute h-3 top-0.5 bg-orange-500/90 rounded-full flex items-center pointer-events-auto ${
                            isSelected 
                              ? 'ring-2 ring-white z-50 shadow-lg shadow-orange-500/30 cursor-grab' 
                              : 'opacity-90 hover:opacity-100'
                          }`}
                          style={{
                            left: `${txt.startTime * zoom}px`,
                            width: `${txt.duration * zoom}px`,
                            minWidth: '30px',
                            touchAction: 'none',
                            flexShrink: 0
                          }}
                        >
                          {isSelected && (
                            <>
                              <div
                                onTouchStart={(e) => { e.stopPropagation(); handleTextInteraction(e, txt.id, 'trim-left'); }}
                                onMouseDown={(e) => { e.stopPropagation(); handleTextInteraction(e, txt.id, 'trim-left'); }}
                                className="absolute -left-1 top-0 bottom-0 w-4 bg-white rounded-l-sm flex items-center justify-center z-[60] shadow-md cursor-ew-resize"
                                style={{ touchAction: 'none' }}
                              >
                                <div className="w-0.5 h-3 bg-zinc-400" />
                              </div>
                              <div
                                onTouchStart={(e) => { e.stopPropagation(); handleTextInteraction(e, txt.id, 'trim-right'); }}
                                onMouseDown={(e) => { e.stopPropagation(); handleTextInteraction(e, txt.id, 'trim-right'); }}
                                className="absolute -right-1 top-0 bottom-0 w-4 bg-white rounded-r-sm flex items-center justify-center z-[60] shadow-md cursor-ew-resize"
                                style={{ touchAction: 'none' }}
                              >
                                <div className="w-0.5 h-3 bg-zinc-400" />
                              </div>
                              <div className={`absolute -top-6 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold whitespace-nowrap ${isTextTrimming ? 'animate-pulse scale-110' : ''}`}>
                                {txt.duration.toFixed(1)}s
                              </div>
                            </>
                          )}
                          <Type size={10} className="text-white ml-4 flex-shrink-0" />
                          <span className="text-[9px] text-white font-black ml-2 truncate pr-4 uppercase">
                            {txt.content || "Text"}
                          </span>
                        </div>
                      );
                    })
                  ) : null}
                </div>

                {/* ═══ SPUR 3: AUDIO (ISOLIERT) ═══ */}
                {/* 🎯 pointer-events-none auf Container verhindert Geister-Berührungen */}
                <div
                  className="relative h-4 bg-zinc-900/10 rounded-sm overflow-visible pointer-events-none"
                  style={{ 
                    width: `${totalDuration * zoom}px`, 
                    minWidth: '100px',
                    flexShrink: 0  // ⚡ Verhindert Zusammendrücken!
                  }}
                >
                  {audioTracks.length > 0 ? (
                    audioTracks.map(track => {
                      const isSelected = selectedAudioId === track.id;
                      return (
                        <div
                          key={track.id}
                          // 🎯 MOVE: Mitte anfassen zum Verschieben (nur wenn selected)
                          onMouseDown={(e) => { if (!isSelected) return; handleAudioInteraction(e, track.id, 'move'); }}
                          onTouchStart={(e) => { if (!isSelected) return; handleAudioInteraction(e, track.id, 'move'); }}
                          onClick={(e) => { e.stopPropagation(); selectAudio(track.id); }}
                          className={`absolute h-3 top-0.5 bg-blue-600/80 rounded-full flex items-center pointer-events-auto ${
                            isSelected 
                              ? 'ring-2 ring-white z-50 shadow-xl cursor-grab' 
                              : 'opacity-80 hover:opacity-100'
                          }`}
                          style={{
                            left: `${track.startTime * zoom}px`,
                            width: `${track.duration * zoom}px`,
                            minWidth: '20px',
                            touchAction: 'none',
                            flexShrink: 0  // ⚡ Keine Schrumpfung!
                          }}
                        >
                          {/* 🎯 ÜBERGROSSE GRIFFE (besseres Greifen + präzise Hitbox) */}
                          {isSelected && (
                            <>
                              {/* Linker Griff - TRIM LEFT */}
                              <div
                                onTouchStart={(e) => { e.stopPropagation(); handleAudioInteraction(e, track.id, 'trim-left'); }}
                                onMouseDown={(e) => { e.stopPropagation(); handleAudioInteraction(e, track.id, 'trim-left'); }}
                                className="absolute -left-2 top-0 bottom-0 w-6 bg-white rounded-sm flex items-center justify-center z-[60] shadow-md cursor-ew-resize"
                                style={{ touchAction: 'none' }}
                              >
                                <div className="w-0.5 h-4 bg-zinc-400" />
                              </div>
                              {/* Rechter Griff - TRIM RIGHT */}
                              <div
                                onTouchStart={(e) => { e.stopPropagation(); handleAudioInteraction(e, track.id, 'trim-right'); }}
                                onMouseDown={(e) => { e.stopPropagation(); handleAudioInteraction(e, track.id, 'trim-right'); }}
                                className="absolute -right-2 top-0 bottom-0 w-6 bg-white rounded-sm flex items-center justify-center z-[60] shadow-md cursor-ew-resize"
                                style={{ touchAction: 'none' }}
                              >
                                <div className="w-0.5 h-4 bg-zinc-400" />
                              </div>
                              {/* Dauer Anzeige */}
                              <div className={`absolute -top-7 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold whitespace-nowrap ${isAudioTrimming ? 'animate-pulse scale-110' : ''}`}>
                                {track.duration.toFixed(2)}s
                              </div>
                            </>
                          )}
                          
                          {/* Icon + Name */}
                          <Music size={12} className="text-white ml-5 flex-shrink-0" />
                          <span className="text-[9px] text-white font-bold ml-2 truncate pr-2">{track.name}</span>
                          
                          {/* 🎵 ECHTE WAVEFORM (Zoom-synchron) */}
                          <div 
                            className="absolute bottom-0 left-5 right-5 h-full flex items-center pointer-events-none overflow-hidden"
                            style={{ opacity: track.waveformPeaks ? 0.6 : 0.3 }}
                          >
                            {track.waveformPeaks ? (
                              // Echte Peaks - Anzahl Balken = sichtbare Breite / 3px
                              (() => {
                                const visibleWidth = track.duration * zoom;
                                const barWidth = 2;
                                const gap = 1;
                                const numBars = Math.floor(visibleWidth / (barWidth + gap));
                                const peaks = track.waveformPeaks;
                                const step = peaks.length / numBars;
                                
                                return Array.from({ length: Math.min(numBars, 10000) }).map((_, i) => {
                                  const peakIndex = Math.floor(i * step);
                                  const amplitude = peaks[peakIndex] || 0;
                                  // Minimum 15%, Maximum 100%
                                  const height = 15 + amplitude * 85;
                                  
                                  return (
                                    <div 
                                      key={i} 
                                      className="bg-white/80 flex-shrink-0"
                                      style={{ 
                                        width: `${barWidth}px`,
                                        height: `${height}%`,
                                        marginRight: `${gap}px`,
                                        borderRadius: '1px'
                                      }} 
                                    />
                                  );
                                });
                              })()
                            ) : (
                              // Fallback: Lade-Animation
                              <div className="w-full h-full flex items-center justify-center">
                                <div className="text-[8px] text-white/50">Analysiere...</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      onClick={() => setShowMusicLibrary(true)}
                      className="flex items-center h-full px-4 text-zinc-500 gap-2 cursor-pointer hover:text-zinc-400 pointer-events-auto"
                    >
                      <Music size={12} />
                      <span className="text-[9px] font-bold uppercase">Audio hinzufügen</span>
                    </div>
                  )}
                </div>

              </div>

              {/* End-Padding */}
              <div style={{ minWidth: '50vw' }} className="flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* 🎛️ KONTEXT-TOOLBAR (erscheint wenn Audio/Clip/Text ausgewählt) */}
        {(selectedAudioId || selectedClipId || selectedTextId) && (
          <div className="flex items-center justify-center gap-6 py-2 bg-zinc-950 border-t border-white/10">
            {/* Bearbeiten (nur Text) */}
            {selectedTextId && (
              <button
                onClick={() => {
                  setIsEditingText(selectedTextId);
                  if (navigator.vibrate) navigator.vibrate(10);
                }}
                className="flex flex-col items-center gap-0.5 text-orange-400 hover:text-orange-300 active:scale-95 transition-all"
              >
                <Type size={18} />
                <span className="text-[9px]">Bearbeiten</span>
              </button>
            )}
            
            {/* 🪄 ANIMIEREN (nur für Bild-Clips, nicht Videos!) */}
            {selectedClipId && (() => {
              const selectedClip = clips.find(c => c.id === selectedClipId);
              const isImage = selectedClip && selectedClip.type !== 'video';
              const isGenerating = selectedClip?.isGenerating;
              
              if (!isImage) return null;
              
              return (
                <button
                  onClick={() => {
                    if (!isGenerating) {
                      animateClip(selectedClipId);
                    }
                    if (navigator.vibrate) navigator.vibrate(15);
                  }}
                  disabled={isGenerating}
                  className={`flex flex-col items-center gap-0.5 active:scale-95 transition-all ${
                    isGenerating 
                      ? 'text-purple-400 animate-pulse cursor-wait' 
                      : 'text-purple-400 hover:text-purple-300'
                  }`}
                >
                  <Sparkles size={18} className={isGenerating ? 'animate-spin' : ''} />
                  <span className="text-[9px]">
                    {isGenerating ? `${selectedClip.generationProgress || 0}%` : 'Animieren'}
                  </span>
                </button>
              );
            })()}
            
            {/* Teilen (Audio/Clips) */}
            {(selectedAudioId || selectedClipId) && (
              <button
                onClick={() => {
                  if (selectedAudioId) splitAudio();
                  if (navigator.vibrate) navigator.vibrate(10);
                }}
                className="flex flex-col items-center gap-0.5 text-zinc-400 hover:text-white active:scale-95 transition-all"
              >
                <Scissors size={18} />
                <span className="text-[9px]">Teilen</span>
              </button>
            )}
            
            {/* Lautstärke (nur Audio) */}
            {selectedAudioId && (
              <button
                onClick={() => {
                  if (navigator.vibrate) navigator.vibrate(10);
                }}
                className="flex flex-col items-center gap-0.5 text-zinc-400 hover:text-white active:scale-95 transition-all"
              >
                <Volume2 size={18} />
                <span className="text-[9px]">Lautstärke</span>
              </button>
            )}
            
            {/* Duplizieren */}
            <button
              onClick={() => {
                if (navigator.vibrate) navigator.vibrate(10);
              }}
              className="flex flex-col items-center gap-0.5 text-zinc-400 hover:text-white active:scale-95 transition-all"
            >
              <Layers size={18} />
              <span className="text-[9px]">Duplizieren</span>
            </button>
            
            {/* Löschen */}
            <button
              onClick={() => {
                if (selectedAudioId) deleteSelectedAudio();
                if (selectedTextId) removeTextTrack(selectedTextId);
                if (navigator.vibrate) navigator.vibrate(20);
              }}
              className="flex flex-col items-center gap-0.5 text-red-400 hover:text-red-300 active:scale-95 transition-all"
            >
              <Trash2 size={18} />
              <span className="text-[9px]">Löschen</span>
            </button>
          </div>
        )}

        {/* E. Bottom Toolbar ENTFERNT - Tools sind jetzt in der Apple-Style Sidebar rechts! */}
        {/* Safe-Area Padding für iOS Home-Indicator */}
        <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 4px)' }} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* 🎵 MUSIK-BIBLIOTHEK MODAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {showMusicLibrary && (
        <div className="fixed inset-0 z-[10001] bg-black/95 flex flex-col animate-in slide-in-from-bottom duration-300">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-white/10">
            <h2 className="text-white text-lg font-bold">🎵 Musik Bibliothek</h2>
            <button 
              onClick={() => setShowMusicLibrary(false)}
              className="p-2 bg-zinc-800 rounded-full hover:bg-zinc-700"
            >
              <X size={20} className="text-white" />
            </button>
          </div>

          {/* Song Liste */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {[
              { id: 'm1', name: 'Phonk Drift', duration: 120, bpm: 130, url: '/storage/music/phonk_drift.mp3' },
              { id: 'm2', name: 'Chill Lofi', duration: 150, bpm: 85, url: '/storage/music/chill_lofi.mp3' },
              { id: 'm3', name: 'Epic Cinematic', duration: 180, bpm: 80, url: '/storage/music/epic_cinematic.mp3' },
              { id: 'm4', name: 'Techno Beat', duration: 140, bpm: 140, url: '/storage/music/techno_beat.mp3' },
              { id: 'm5', name: 'Fashion Runway', duration: 160, bpm: 120, url: '/storage/music/fashion_runway.mp3' },
            ].map(song => (
              <div
                key={song.id}
                onClick={() => addAudioTrack(song)}
                className="flex items-center justify-between p-4 bg-zinc-900/80 rounded-xl active:bg-zinc-800 transition-all cursor-pointer border border-white/5"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                    <Music className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-white font-bold">{song.name}</p>
                    <p className="text-zinc-500 text-xs">{Math.floor(song.duration / 60)}:{(song.duration % 60).toString().padStart(2, '0')} • {song.bpm} BPM</p>
                  </div>
                </div>
                <button className="bg-blue-500 text-white px-4 py-1.5 rounded-full text-xs font-bold hover:bg-blue-400 transition-colors">
                  Hinzufügen
                </button>
              </div>
            ))}
          </div>

          {/* Info Text */}
          <div className="p-4 text-center text-zinc-500 text-xs border-t border-white/10">
            Die Musik startet bei der aktuellen Playhead-Position ({masterTimeRef.current?.toFixed(1) || 0}s)
          </div>
        </div>
      )}

      {/* 🪄 ANIMATE MODAL - Auto/Manual Auswahl */}
      {animateModal.open && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 w-full max-w-md space-y-4 border border-white/10">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-400" />
              KI-Animation
            </h3>
            
            <p className="text-zinc-400 text-sm">
              Kosten: ~5 SNAPs ($0.39) • Dauer: ~10 Sekunden Video
            </p>

            {/* Bewegung beschreiben */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white font-medium">Bewegung beschreiben:</span>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={generatePromptSuggestion}
                    disabled={isGeneratingPrompt}
                    className="text-xs px-3 py-1.5 rounded-lg bg-purple-600/30 hover:bg-purple-600/50 text-purple-300 border border-purple-500/30 flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {isGeneratingPrompt ? (
                      <>
                        <div className="w-3 h-3 border-2 border-purple-300 border-t-transparent rounded-full animate-spin" />
                        Analysiere...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        Vorschlag
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => setAnimateLang(animateLang === 'de' ? 'en' : 'de')}
                    className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                  >
                    {animateLang === 'de' ? '🇩🇪 DE' : '🇬🇧 EN'}
                  </button>
                </div>
              </div>
              <textarea
                value={animatePrompt}
                onChange={(e) => setAnimatePrompt(e.target.value)}
                placeholder={animateLang === 'de' 
                  ? "Klicke 'Vorschlag' oder beschreibe selbst: Lächelt sanft, dreht den Kopf..."
                  : "Click 'Vorschlag' or describe: Gentle smile, slowly turns head..."
                }
                className="w-full p-3 rounded-xl bg-black/50 border border-white/10 text-white placeholder:text-zinc-600 resize-none"
                rows={4}
                autoFocus
              />
              <p className="text-xs text-zinc-500">
                💡 Klicke "Vorschlag" für KI-Analyse, dann passe an. Wir optimieren alles automatisch!
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setAnimateModal({ open: false, clipId: null })}
                className="flex-1 py-3 rounded-xl border border-white/10 text-white hover:bg-white/5"
              >
                Abbrechen
              </button>
              <button
                onClick={startAnimation}
                disabled={!animatePrompt?.trim()}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Animieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✨ MAGIC CREATE OVERLAY - NUR für Master-Admin */}
      {isMasterAdmin && showMagicCreate && (
        <div className="fixed inset-0 z-[20000]">
          <MagicCreate
            avatarId={avatarId}
            onBack={() => setShowMagicCreate(false)}
            onComplete={(result) => {
              console.log('Magic complete:', result);
              setShowMagicCreate(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
